import React from "react";
import { Box, Flex, Text, useBreakpointValue } from "@chakra-ui/react";
import { OportunityContext } from "@context/oportunityContex";
import { theme } from "src/styles/theme";

interface IStep {
  title: string;
  icon: any;
  states: string[];
}

interface SteppProps {
  steps: IStep[];
  progressPercent: number;
  activeStep: string;
}

const Stepp: React.FC<SteppProps> = ({
  steps,
  activeStep,
  progressPercent,
}) => {
  const [completedSteps, setCompletedSteps] = React.useState<string[]>([]);
  const { setCurrentFase } = React.useContext(OportunityContext);

  // Responsive sizes
  const stepSize = useBreakpointValue({ base: "35px", md: "45px" });
  const fontSize = useBreakpointValue({ base: "11px", md: "15px" });
  const progressTop = useBreakpointValue({ base: "10px", md: "15px" });
  const progressWidth = useBreakpointValue({
    base: "xl",
    sm: "xl",
    md: "90%",
  });
  const iconFontSize = useBreakpointValue({ base: "17px", md: "24px" });

  React.useEffect(() => {
    const activeStepIndex = steps.findIndex((step) =>
      step.states.includes(activeStep?.toLowerCase())
    );

    if (activeStepIndex !== -1) {
      const updatedSteps = steps
        .slice(0, activeStepIndex + 1)
        .map((step) => step.title);
      setCompletedSteps(updatedSteps);
    } else {
      setCompletedSteps([]);
    }
  }, [activeStep, steps]);

  React.useEffect(() => {
    setCurrentFase(activeStep);
  }, [activeStep, setCurrentFase]);

  const activeStepIndex = steps.findIndex((step) =>
    step.states.includes(activeStep?.toLowerCase())
  );

  const getProgressPercentByStep = (step: string) => {
    // Encontrar o índice do passo atual
    const currentStepIndex = steps.findIndex((s) =>
      s.states.includes(step?.toLowerCase())
    );

    // Se não encontrou o passo ou é o último passo, retorna 100%
    if (currentStepIndex === -1 || currentStepIndex >= steps.length - 1) {
      return 100;
    }

    // Calcular a porcentagem com base na posição do próximo passo
    // Cada passo representa uma fração igual do progresso total
    const nextStepPosition = currentStepIndex;
    return (nextStepPosition / (steps.length - 1)) * 100;
  };

  // Garantir que progressPercent seja 0 quando for null
  const safeProgressPercent = progressPercent === null ? 0 : progressPercent;

  const progressPercentManual = getProgressPercentByStep(activeStep);

  // Helper functions to determine colors based on step status
  const getStepBackgroundColor = (step: IStep) => {
    if (activeStep?.toLowerCase() === "fechado e perdido") return theme.error;
    if (step.title?.toLowerCase() === activeStep?.toLowerCase())
      return theme.primary;
    if (completedSteps.includes(step.title)) return theme.primary;
    return "white";
  };

  const getStepTextColor = (step: IStep) => {
    if (activeStep?.toLowerCase() === "fechado e perdido") return "white";
    if (step.title?.toLowerCase() === activeStep?.toLowerCase()) return "white";
    if (completedSteps.includes(step.title)) return "white";
    return theme.primary;
  };

  const getTitleColor = (step: IStep) => {
    if (activeStep?.toLowerCase() === "fechado e perdido") return theme.text;
    if (step.title?.toLowerCase() === activeStep?.toLowerCase())
      return theme.text;
    if (completedSteps.includes(step.title)) return theme.text;
    return theme.disable;
  };

  // Calcular a porcentagem para exibição no último círculo
  const displayPercent = safeProgressPercent;

  return (
    <Flex position="relative">
      {steps.map((step, index) => (
        <Flex key={step.title} direction="column" alignItems="center">
          <Box textAlign="center" marginRight="10px" zIndex={2}>
            <Flex
              width={stepSize}
              height={stepSize}
              borderRadius="50%"
              boxShadow="rgba(0, 0, 0, 0.25) 0px 5px 15px"
              backgroundColor={getStepBackgroundColor(step)}
              color={getStepTextColor(step)}
              alignItems="center"
              justifyContent="center"
              fontWeight="bold"
            >
              {index === steps.length - 1
                ? `${displayPercent}%`
                : React.isValidElement(step.icon)
                  ? React.cloneElement(step.icon)
                  : step.icon}
            </Flex>
          </Box>

          <Box>
            <Text
              display="flex"
              width={{ base: "80px", md: "85%" }}
              fontSize={fontSize}
              fontWeight="medium"
              color={getTitleColor(step)}
            >
              {step.title}
            </Text>
          </Box>
        </Flex>
      ))}

      <Box
        position="absolute"
        top={progressTop}
        height="15px"
        width={progressWidth}
        backgroundColor="#002a6d1a"
        zIndex={1}
      >
        <Box
          position="absolute"
          height="100%"
          width={`${progressPercentManual}%`}
          backgroundColor={
            activeStep?.toLowerCase() === "fechado e perdido"
              ? theme.error
              : theme.backgroundPrimary
          }
          transition="width 0.3s ease"
          borderRadius="30px"
        />
      </Box>
    </Flex>
  );
};

export default Stepp;
